import React, { useState, useEffect, useCallback } from 'react';
import { reportsService } from '../services/api';
import DynamicReportsTable from './DynamicReportsTable';
import { toast } from 'react-toastify';
import './CustomReports.css';

const CustomReports = () => {
  // State for schema data
  const [schema, setSchema] = useState(null);
  const [loadingSchema, setLoadingSchema] = useState(true);

  // State for query builder
  const [selectedEntity, setSelectedEntity] = useState('');
  const [selectedFields, setSelectedFields] = useState([]);
  const [selectedRelationships, setSelectedRelationships] = useState({});
  const [filters, setFilters] = useState({});
  const [orderBy, setOrderBy] = useState({});
  const [limit, setLimit] = useState(100);
  const [page, setPage] = useState(1);

  // State for report data
  const [reportData, setReportData] = useState([]);
  const [reportColumns, setReportColumns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState(null);

  // UI State
  const [showQueryBuilder, setShowQueryBuilder] = useState(true);

  // Fetch schema on component mount
  useEffect(() => {
    fetchSchema();
  }, []);

  const fetchSchema = async () => {
    try {
      setLoadingSchema(true);
      const response = await reportsService.getSchema();
      
      if (response.success) {
        setSchema(response.data);
        toast.success('Schema loaded successfully');
      } else {
        toast.error('Failed to load schema');
      }
    } catch (error) {
      console.error('Error fetching schema:', error);
      toast.error('Error loading schema: ' + (error.message || 'Unknown error'));
    } finally {
      setLoadingSchema(false);
    }
  };

  // Get entity details from schema
  const getEntityDetails = useCallback((entityName) => {
    if (!schema) return null;
    return schema.entities.find(e => e.name === entityName);
  }, [schema]);

  // Handle entity selection
  const handleEntityChange = (entityName) => {
    setSelectedEntity(entityName);
    setSelectedFields([]);
    setSelectedRelationships({});
    setFilters({});
    setOrderBy({});
    setReportData([]);
    setReportColumns([]);
  };

  // Toggle field selection
  const toggleField = (fieldName) => {
    setSelectedFields(prev => {
      if (prev.includes(fieldName)) {
        return prev.filter(f => f !== fieldName);
      } else {
        return [...prev, fieldName];
      }
    });
  };

  // Toggle relationship inclusion
  const toggleRelationship = (relationName) => {
    setSelectedRelationships(prev => {
      const newRels = { ...prev };
      if (newRels[relationName]) {
        delete newRels[relationName];
      } else {
        newRels[relationName] = true; // Include all fields by default
      }
      return newRels;
    });
  };

  // Configure relationship fields
  const configureRelationshipFields = (relationName, fields) => {
    setSelectedRelationships(prev => ({
      ...prev,
      [relationName]: fields.length > 0 ? { fields } : true
    }));
  };

  // Add filter
  const addFilter = (fieldName, operator, value) => {
    setFilters(prev => ({
      ...prev,
      [fieldName]: operator === 'equals' ? value : { [operator]: value }
    }));
  };

  // Remove filter
  const removeFilter = (fieldName) => {
    setFilters(prev => {
      const newFilters = { ...prev };
      delete newFilters[fieldName];
      return newFilters;
    });
  };

  // Add sort order
  const addSortOrder = (fieldName, direction) => {
    setOrderBy({ [fieldName]: direction });
  };

  // Generate report
  const generateReport = async () => {
    if (!selectedEntity) {
      toast.error('Please select an entity');
      return;
    }

    try {
      setLoading(true);

      const queryConfig = {
        entity: selectedEntity,
        ...(selectedFields.length > 0 && { fields: selectedFields }),
        ...(Object.keys(selectedRelationships).length > 0 && { include: selectedRelationships }),
        ...(Object.keys(filters).length > 0 && { filters }),
        ...(Object.keys(orderBy).length > 0 && { orderBy }),
        limit,
        page
      };

      console.log('Query Config:', queryConfig);

      const response = await reportsService.getCustomReport(queryConfig);

      if (response.success) {
        setReportData(response.data);
        setPagination(response.pagination);
        
        // Generate columns from first record
        if (response.data.length > 0) {
          const columns = generateColumnsFromData(response.data[0]);
          setReportColumns(columns);
        }

        toast.success(`Report generated: ${response.meta.recordsReturned} records`);
        setShowQueryBuilder(false);
      } else {
        toast.error('Failed to generate report');
      }
    } catch (error) {
      console.error('Error generating report:', error);
      toast.error('Error generating report: ' + (error.message || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  };

  // Generate columns dynamically from data
  const generateColumnsFromData = (record, prefix = '') => {
    const columns = [];

    Object.keys(record).forEach(key => {
      const value = record[key];
      const columnKey = prefix ? `${prefix}.${key}` : key;

      // Skip arrays and complex objects for now (will be expandable)
      if (Array.isArray(value)) {
        columns.push({
          key: columnKey,
          label: formatLabel(key),
          sortable: false
        });
      } else if (value !== null && typeof value === 'object') {
        // Nested object - don't create sub-columns, make it expandable
        columns.push({
          key: columnKey,
          label: formatLabel(key),
          sortable: false
        });
      } else {
        // Primitive value
        const columnDef = {
          key: columnKey,
          label: formatLabel(key),
          sortable: true
        };

        // Detect column type
        if (key.toLowerCase().includes('price') || key.toLowerCase().includes('amount') || 
            key.toLowerCase().includes('fee') || key.toLowerCase().includes('total')) {
          columnDef.type = 'currency';
          columnDef.align = 'right';
        } else if (key.toLowerCase().includes('date') || key.toLowerCase().includes('time') ||
                   key.toLowerCase().includes('createdat') || key.toLowerCase().includes('updatedat')) {
          columnDef.type = 'date';
        } else if (key.toLowerCase().includes('percent') || key.toLowerCase().includes('percentage')) {
          columnDef.type = 'percentage';
        } else if (typeof value === 'boolean') {
          columnDef.formatter = (v) => v ? '✓' : '✗';
          columnDef.align = 'center';
        }

        columns.push(columnDef);
      }
    });

    return columns;
  };

  // Format field name to readable label
  const formatLabel = (fieldName) => {
    return fieldName
      .replace(/([A-Z])/g, ' $1')
      .replace(/^./, str => str.toUpperCase())
      .trim();
  };

  // Reset query builder
  const resetQueryBuilder = () => {
    setSelectedEntity('');
    setSelectedFields([]);
    setSelectedRelationships({});
    setFilters({});
    setOrderBy({});
    setLimit(100);
    setPage(1);
    setReportData([]);
    setReportColumns([]);
    setShowQueryBuilder(true);
  };

  if (loadingSchema) {
    return (
      <div className="custom-reports-container">
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Loading schema...</p>
        </div>
      </div>
    );
  }

  if (!schema) {
    return (
      <div className="custom-reports-container">
        <div className="error-container">
          <p>Failed to load schema. Please refresh the page.</p>
          <button onClick={fetchSchema} className="btn-primary">Retry</button>
        </div>
      </div>
    );
  }

  const entityDetails = selectedEntity ? getEntityDetails(selectedEntity) : null;

  return (
    <div className="custom-reports-container">
      <div className="custom-reports-header">
        <div className="header-left">
          <h2>Custom Report Builder</h2>
          <p className="subtitle">Build dynamic reports by selecting entity, fields, and relationships</p>
        </div>
        <div className="header-actions">
          {reportData.length > 0 && (
            <>
              <button 
                onClick={() => setShowQueryBuilder(!showQueryBuilder)} 
                className="btn-secondary"
              >
                {showQueryBuilder ? 'View Report' : 'Edit Query'}
              </button>
              <button onClick={resetQueryBuilder} className="btn-outline">
                New Report
              </button>
            </>
          )}
        </div>
      </div>

      {showQueryBuilder ? (
        <div className="query-builder">
          {/* Step 1: Select Entity */}
          <div className="query-section">
            <div className="section-header">
              <h3>Select Entity</h3>
              <span className="section-info">{schema.entities.length} entities available</span>
            </div>
            <div className="entity-grid">
              {schema.entities.map(entity => (
                <div
                  key={entity.name}
                  className={`entity-card ${selectedEntity === entity.name ? 'selected' : ''}`}
                  onClick={() => handleEntityChange(entity.name)}
                >
                  <div className="entity-name">{entity.name}</div>
                  <div className="entity-description">{entity.description}</div>
                  <div className="entity-meta">
                    <span className="category-badge">{entity.category}</span>
                    <span className="field-count">{entity.fields.length} fields</span>
                    <span className="relation-count">{entity.relationships.length} relations</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {selectedEntity && entityDetails && (
            <>
              {/* Step 2: Select Fields */}
              <div className="query-section">
                <div className="section-header">
                  <h3>Select Fields</h3>
                  <span className="section-info">
                    {selectedFields.length > 0 
                      ? `${selectedFields.length} fields selected` 
                      : 'Select fields or leave empty for all'}
                  </span>
                  <button 
                    className="btn-link"
                    onClick={() => {
                      if (selectedFields.length === entityDetails.fields.length) {
                        setSelectedFields([]);
                      } else {
                        setSelectedFields(entityDetails.fields.map(f => f.name));
                      }
                    }}
                  >
                    {selectedFields.length === entityDetails.fields.length ? 'Deselect All' : 'Select All'}
                  </button>
                </div>
                <div className="fields-grid">
                  {entityDetails.fields.map(field => (
                    <div
                      key={field.name}
                      className={`field-checkbox ${selectedFields.includes(field.name) ? 'checked' : ''}`}
                      onClick={() => toggleField(field.name)}
                    >
                      <input
                        type="checkbox"
                        checked={selectedFields.includes(field.name)}
                        readOnly
                      />
                      <div className="field-info">
                        <span className="field-name">{field.name}</span>
                        <span className="field-type">{field.type}</span>
                      </div>
                      <div className="field-description">{field.description}</div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Step 3: Select Relationships */}
              {entityDetails.relationships.length > 0 && (
                <div className="query-section">
                  <div className="section-header">
                    <h3>Include Relationships</h3>
                    <span className="section-info">
                      {Object.keys(selectedRelationships).length} relationships included
                    </span>
                  </div>
                  <div className="relationships-list">
                    {entityDetails.relationships.map(relation => {
                      const isIncluded = !!selectedRelationships[relation.name];
                      const targetEntity = getEntityDetails(relation.targetEntity);

                      return (
                        <div key={relation.name} className="relationship-card">
                          <div className="relationship-header">
                            <div 
                              className="relationship-toggle"
                              onClick={() => toggleRelationship(relation.name)}
                            >
                              <input
                                type="checkbox"
                                checked={isIncluded}
                                readOnly
                              />
                              <div className="relationship-info">
                                <span className="relationship-name">{relation.name}</span>
                                <span className="relationship-arrow">→</span>
                                <span className="target-entity">{relation.targetEntity}</span>
                                <span className="relationship-type">{relation.type}</span>
                              </div>
                            </div>
                          </div>
                          
                          {isIncluded && targetEntity && (
                            <div className="relationship-fields">
                              <div className="fields-selector">
                                <span className="fields-label">Select fields:</span>
                                <button
                                  className="btn-link-sm"
                                  onClick={() => {
                                    const currentRel = selectedRelationships[relation.name];
                                    if (currentRel === true) {
                                      configureRelationshipFields(relation.name, []);
                                    } else {
                                      setSelectedRelationships(prev => ({
                                        ...prev,
                                        [relation.name]: true
                                      }));
                                    }
                                  }}
                                >
                                  {selectedRelationships[relation.name] === true ? 'All fields' : 'Custom'}
                                </button>
                              </div>
                              
                              {selectedRelationships[relation.name] !== true && (
                                <div className="relation-fields-grid">
                                  {targetEntity.fields.slice(0, 10).map(field => {
                                    const relFields = selectedRelationships[relation.name]?.fields || [];
                                    const isFieldSelected = relFields.includes(field.name);

                                    return (
                                      <div
                                        key={field.name}
                                        className={`field-chip ${isFieldSelected ? 'selected' : ''}`}
                                        onClick={() => {
                                          const currentFields = selectedRelationships[relation.name]?.fields || [];
                                          const newFields = isFieldSelected
                                            ? currentFields.filter(f => f !== field.name)
                                            : [...currentFields, field.name];
                                          configureRelationshipFields(relation.name, newFields);
                                        }}
                                      >
                                        {field.name}
                                      </div>
                                    );
                                  })}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Step 4: Filters & Sorting */}
              <div className="query-section">
                <div className="section-header">
                  <h3>Filters & Sorting (Optional)</h3>
                  <span className="section-info">
                    {Object.keys(filters).length} filters, {Object.keys(orderBy).length > 0 ? '1 sort' : 'no sorting'}
                  </span>
                </div>
                
                <div className="filters-sorting-container">
                  <div className="filter-builder">
                    <h4>Filters</h4>
                    <div className="filter-inputs">
                      <select className="form-select">
                        <option value="">Select field...</option>
                        {entityDetails.fields
                          .filter(f => !f.systemField)
                          .map(f => (
                            <option key={f.name} value={f.name}>{f.name}</option>
                          ))
                        }
                      </select>
                      <select className="form-select">
                        <option value="equals">Equals</option>
                        <option value="contains">Contains</option>
                        <option value="gte">Greater or Equal</option>
                        <option value="lte">Less or Equal</option>
                        <option value="in">In Array</option>
                      </select>
                      <input type="text" className="form-input" placeholder="Value..." />
                      <button className="btn-primary-sm">Add Filter</button>
                    </div>
                    
                    {Object.keys(filters).length > 0 && (
                      <div className="active-filters">
                        {Object.entries(filters).map(([field, value]) => (
                          <div key={field} className="filter-tag">
                            <span>{field}: {JSON.stringify(value)}</span>
                            <button onClick={() => removeFilter(field)}>×</button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  <div className="sort-builder">
                    <h4>Sort Order</h4>
                    <div className="sort-inputs">
                      <select 
                        className="form-select"
                        value={Object.keys(orderBy)[0] || ''}
                        onChange={(e) => {
                          if (e.target.value) {
                            addSortOrder(e.target.value, Object.values(orderBy)[0] || 'asc');
                          } else {
                            setOrderBy({});
                          }
                        }}
                      >
                        <option value="">No sorting</option>
                        {entityDetails.fields.map(f => (
                          <option key={f.name} value={f.name}>{f.name}</option>
                        ))}
                      </select>
                      {Object.keys(orderBy).length > 0 && (
                        <select 
                          className="form-select"
                          value={Object.values(orderBy)[0]}
                          onChange={(e) => addSortOrder(Object.keys(orderBy)[0], e.target.value)}
                        >
                          <option value="asc">Ascending</option>
                          <option value="desc">Descending</option>
                        </select>
                      )}
                    </div>
                  </div>

                  <div className="pagination-builder">
                    <h4>Limit</h4>
                    <input 
                      type="number" 
                      className="form-input"
                      value={limit}
                      onChange={(e) => setLimit(Math.max(1, parseInt(e.target.value) || 100))}
                      min="1"
                      max="1000"
                    />
                  </div>
                </div>
              </div>

              {/* Generate Button */}
              <div className="query-actions">
                <button 
                  onClick={generateReport} 
                  className="btn-generate"
                  disabled={loading}
                >
                  {loading ? 'Generating...' : 'Generate Report'}
                </button>
              </div>
            </>
          )}
        </div>
      ) : (
        // Report View
        <div className="report-view">
          {reportData.length > 0 ? (
            <>
              <div className="report-meta">
                <div className="meta-item">
                  <span className="meta-label">Entity:</span>
                  <span className="meta-value">{selectedEntity}</span>
                </div>
                <div className="meta-item">
                  <span className="meta-label">Records:</span>
                  <span className="meta-value">{reportData.length}</span>
                </div>
                {pagination && (
                  <div className="meta-item">
                    <span className="meta-label">Page:</span>
                    <span className="meta-value">{pagination.page} of {pagination.totalPages}</span>
                  </div>
                )}
              </div>

              <DynamicReportsTable
                data={reportData}
                columns={reportColumns}
                title={`${selectedEntity} Report`}
                exportFileName={`custom_${selectedEntity}_report`}
              />
            </>
          ) : (
            <div className="no-data">
              <p>No data available. Please adjust your query.</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CustomReports;
